import { EmptyState, ListSkeleton } from '@atlas/ui-components'
import { Button, DialogContent, DialogTitle } from '@atlas/ui-primitives'
import type { CampaignFile } from '@/domain/campaign.types'

export interface CampaignFilePickerCandidate {
	file: CampaignFile
	eligible: boolean
}

export interface CampaignFilePickerDialogProps {
	isOpen: boolean
	candidates: readonly CampaignFilePickerCandidate[]
	isLoading: boolean
	isError: boolean
	search: string
	onSearchChange: (value: string) => void
	selectedIds: ReadonlySet<string>
	onToggleSelection: (fileId: string) => void
	isAssociationPending: boolean
	isAssociationError: boolean
	isAssociationSuccess: boolean
	onConfirm: () => void
	onRetry: () => void
	onCancel: () => void
}

function CandidateList({
	candidates,
	selectedIds,
	onToggleSelection,
}: {
	candidates: readonly CampaignFilePickerCandidate[]
	selectedIds: ReadonlySet<string>
	onToggleSelection: (fileId: string) => void
}) {
	return (
		<ul
			role="listbox"
			aria-multiselectable="true"
			aria-label="Candidate Files"
			className="max-h-60 space-y-1 overflow-auto"
		>
			{candidates.map(({ file, eligible }) => (
				<li key={file.fileId}>
					<label
						className={
							eligible
								? 'flex items-center gap-2 rounded-ds-sm px-2 py-1.5 text-sm text-ds-text hover:bg-ds-surface'
								: 'flex items-center gap-2 rounded-ds-sm px-2 py-1.5 text-sm text-ds-text-subtle opacity-60'
						}
					>
						<input
							type="checkbox"
							checked={selectedIds.has(file.fileId)}
							disabled={!eligible}
							aria-disabled={!eligible}
							onChange={() => onToggleSelection(file.fileId)}
						/>
						{file.fileName}
						{!eligible && <span className="text-xs">(ineligible)</span>}
					</label>
				</li>
			))}
		</ul>
	)
}

function PickerFooter({
	selectionCount,
	isAssociationError,
	isAssociationPending,
	onConfirm,
	onRetry,
	onCancel,
}: {
	selectionCount: number
	isAssociationError: boolean
	isAssociationPending: boolean
	onConfirm: () => void
	onRetry: () => void
	onCancel: () => void
}) {
	return (
		<div className="flex justify-end gap-3">
			<Button variant="outline" onPress={onCancel}>
				Cancel
			</Button>
			{isAssociationError ? (
				<Button onPress={onRetry}>Retry</Button>
			) : (
				<Button onPress={onConfirm} isDisabled={selectionCount === 0 || isAssociationPending}>
					{isAssociationPending ? 'Associating…' : 'Confirm'}
				</Button>
			)}
		</div>
	)
}

/**
 * Existing-Files association picker. Receives candidates, per-candidate
 * eligibility, selection state, search state, and association state entirely
 * as props — calls no hook, imports no fixture. Association is additive-only;
 * a failure can be retried without re-selecting Files.
 */
export function CampaignFilePickerDialog({
	isOpen,
	candidates,
	isLoading,
	isError,
	search,
	onSearchChange,
	selectedIds,
	onToggleSelection,
	isAssociationPending,
	isAssociationError,
	isAssociationSuccess,
	onConfirm,
	onRetry,
	onCancel,
}: CampaignFilePickerDialogProps) {
	return (
		<DialogContent size="lg" isOpen={isOpen} isKeyboardDismissDisabled={isAssociationPending} className="space-y-4">
			<DialogTitle>Browse existing Files</DialogTitle>

			<input
				type="text"
				value={search}
				onChange={(event) => onSearchChange(event.target.value)}
				placeholder="Search Files"
				aria-label="Search Files"
				className="w-full rounded-ds-md border border-ds-border bg-ds-surface px-3 py-2 text-sm text-ds-text"
			/>

			{isLoading && <ListSkeleton rows={3} label="Searching files" />}
			{isError && (
				<p role="alert" className="text-sm text-ds-danger">
					Unable to search for Files.
				</p>
			)}
			{!isLoading && !isError && candidates.length === 0 && (
				<EmptyState title="No Files match your search." variant="filtered" />
			)}
			{!isLoading && !isError && candidates.length > 0 && (
				<CandidateList candidates={candidates} selectedIds={selectedIds} onToggleSelection={onToggleSelection} />
			)}

			<p className="text-sm text-ds-text-muted">{selectedIds.size} selected</p>

			{isAssociationError && (
				<p role="alert" className="text-sm text-ds-danger">
					Associating Files failed.
				</p>
			)}
			{isAssociationSuccess && (
				<p role="status" className="text-sm text-ds-success">
					Files associated.
				</p>
			)}

			<PickerFooter
				selectionCount={selectedIds.size}
				isAssociationError={isAssociationError}
				isAssociationPending={isAssociationPending}
				onConfirm={onConfirm}
				onRetry={onRetry}
				onCancel={onCancel}
			/>
		</DialogContent>
	)
}
